/**
 * SA-1089: what an email thread's bubbles read off the stored rows. Pure,
 * the `groupMessages.ts` precedent.
 *
 * - An attachment is stored as its own `Message` with `content: ""` and
 *   `channel_metadata.email_provider_message_id` naming the email it came
 *   with; the body row owns `provider_message_id`. `attachmentsByBody` joins
 *   them so a bubble shows its files under its text.
 * - `build_turn_text` stores `Subject: …` at the top of every inbound body
 *   and the thread header shows the subject again; `stripSubject` drops the
 *   line when it says what the header says. Nothing stored changes.
 * - `messageHeader` is the one line above an email bubble: the sender, the
 *   full time, and the subject only when it is not the thread's.
 * - An inline `cid:` row (SA-1093) is removed from the list; it and any raster
 *   attachment row carrying a Content-ID (SA-1110) are joined to their body by
 *   `cidRowsByBody`, for the frame.
 */
import type { InboxMessage } from '@/types/inbox'
import { viewableKind } from '@/utils/mediaView'

const str = (value: unknown): string | null => (typeof value === 'string' && value ? value : null)

/** SA-1093 (spec R6): an inline `cid:` part stored as its own row. Never an
 * attachment, never a bubble; the frame reads it by Content-ID. */
export function isInlineRow(m: InboxMessage): boolean {
  return m.channelMetadata?.inline === true
}

/** SA-1110 (design note R3): a row the frame may cite by Content-ID: an
 * inline row, or an ordinary attachment of a raster image type that carries
 * one (Gmail stamps a Content-ID on ordinary files; some clients send an
 * `attachment` image the body still cites). Never a PDF or any other file. */
function isCidRow(m: InboxMessage): boolean {
  if (!m.mediaUrl) return false
  if (isInlineRow(m)) return true
  const meta = m.channelMetadata
  return Boolean(str(meta?.content_id)) && viewableKind(str(meta?.mime_type)) === 'image'
}

/** Each body's cid rows, by body message id, in their stored order. */
export function cidRowsByBody(messages: readonly InboxMessage[]): Map<string, InboxMessage[]> {
  const bodyIdByProvider = new Map<string, string>()
  for (const m of messages) {
    const pid = str(m.channelMetadata?.provider_message_id)
    if (pid && !bodyIdByProvider.has(pid)) bodyIdByProvider.set(pid, m.id)
  }
  const out = new Map<string, InboxMessage[]>()
  for (const m of messages) {
    if (!isCidRow(m)) continue
    const parent = str(m.channelMetadata?.email_provider_message_id)
    const bodyId = parent ? bodyIdByProvider.get(parent) : undefined
    if (!bodyId || bodyId === m.id) continue
    const list = out.get(bodyId) ?? []
    list.push(m)
    out.set(bodyId, list)
  }
  for (const list of out.values()) list.sort((a, b) => a.timestamp - b.timestamp)
  return out
}

export interface ThreadAttachments {
  /** The messages to render, a joined attachment removed. */
  messages: readonly InboxMessage[]
  /** Each body's attachments, by body message id, in their stored order. */
  attachments: Map<string, InboxMessage[]>
}

/** Joins each email attachment row to its body. A thread with no attachment
 * row that names a loaded body comes back as it was given. */
export function attachmentsByBody(messages: readonly InboxMessage[]): ThreadAttachments {
  const bodyIdByProvider = new Map<string, string>()
  for (const m of messages) {
    const pid = str(m.channelMetadata?.provider_message_id)
    if (pid && !bodyIdByProvider.has(pid)) bodyIdByProvider.set(pid, m.id)
  }
  const attachments = new Map<string, InboxMessage[]>()
  const joined = new Set<string>()
  for (const m of messages) {
    if (!m.mediaUrl) continue
    if (isInlineRow(m)) {
      // SA-1093: an inline row is never listed and never a bubble.
      joined.add(m.id)
      continue
    }
    const parent = str(m.channelMetadata?.email_provider_message_id)
    const bodyId = parent ? bodyIdByProvider.get(parent) : undefined
    if (!bodyId || bodyId === m.id) continue
    const list = attachments.get(bodyId) ?? []
    list.push(m)
    attachments.set(bodyId, list)
    joined.add(m.id)
  }
  if (joined.size === 0) return { messages, attachments }
  for (const list of attachments.values()) list.sort((a, b) => a.timestamp - b.timestamp)
  return { messages: messages.filter((m) => !joined.has(m.id)), attachments }
}

const SUBJECT_LINE = /^Subject: ([^\n]*)\n?\n?/

/** Drops the stored `Subject:` line when it equals the thread's subject. */
export function stripSubject(content: string, subject: string | null | undefined): string {
  if (!subject) return content
  const match = SUBJECT_LINE.exec(content)
  if (!match || match[1].trim() !== subject.trim()) return content
  return content.slice(match[0].length)
}

export interface MessageHeaderLine {
  sender: string
  when: string
  /** The message's subject, only when it differs from the thread's. */
  subject: string | null
}

/** The full date and time an email's header line shows (the viewer's zone; `zone` for tests). */
export function fullTime(ts: number, zone?: string): string {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    ...(zone ? { timeZone: zone } : {}),
  })
}

/** The line above an email bubble; null off the email channel or with no
 * sender address on the message. `zone` is for tests; the pane passes none
 * and reads the viewer's zone. */
export function messageHeader(
  message: InboxMessage, threadSubject: string | null | undefined, zone?: string,
): MessageHeaderLine | null {
  if (message.channel !== 'email') return null
  const meta = message.channelMetadata ?? {}
  const addr = str(meta.from)
  if (!addr) return null
  const name = str(meta.from_name)
  const subject = str(meta.subject)
  const changed = subject && (threadSubject ?? '').trim() !== subject.trim() ? subject : null
  return { sender: name ? `${name} <${addr}>` : addr, when: fullTime(message.timestamp, zone), subject: changed }
}

const PLACEHOLDER = /^\[(Image|Document|Video|Sticker|Audio) message received\]$/

/** The WhatsApp pipeline stores "[Image message received]" as the text of
 * a file with no caption; under a stored file it says nothing. */
export function placeholderOnly(message: InboxMessage): boolean {
  return Boolean(message.mediaUrl) && PLACEHOLDER.test((message.content ?? '').trim())
}

/** The date separator: Today, Yesterday, else the date, with the year only
 * outside the current one. `now` is injectable for tests. */
export function separatorLabel(ts: number, now: Date = new Date()): string {
  const date = new Date(ts)
  if (date.toDateString() === now.toDateString()) return 'Today'
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
  const sameYear = date.getFullYear() === now.getFullYear()
  return date.toLocaleDateString([], sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' })
}

/** The full date and time a bubble's `HH:MM` stands for, for its tooltip. */
export function fullTimestamp(ts: number): string {
  return fullTime(ts)
}

/** The words a "new messages" pill shows. */
export function newMessagesLabel(count: number): string {
  return `${count} new message${count === 1 ? '' : 's'}`
}
